import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';

export default function VerifyEmailPending() {
  const location = useLocation();
  const stateEmail = (location.state as { email?: string } | null)?.email ?? '';
  const queryEmail = new URLSearchParams(location.search).get('email') ?? '';
  const [email, setEmail] = useState(stateEmail || queryEmail);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const canResend = useMemo(() => email.trim().length > 0, [email]);

  const handleResend = async () => {
    if (!canResend || isSending) {
      return;
    }

    try {
      setIsSending(true);
      setError('');
      setMessage('');
      const response = await apiClient.post('/api/v1/users/resend-verification', {
        email: email.trim(),
      });
      setMessage(response.data?.message || 'Verification email sent.');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Unable to resend verification email right now.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full">
        <div className="bg-slate-900/85 backdrop-blur-sm shadow-xl rounded-2xl border border-slate-700 p-8 space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-slate-100">Check your email</h1>
            <p className="mt-2 text-slate-300">
              Your account was created. Verify your email to activate your account.
            </p>
          </div>

          <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-4 text-sm text-indigo-200">
            <p>
              We sent a verification link to: <span className="font-semibold">{email || 'your email'}</span>
            </p>
            <p className="mt-2">
              If you do not see it, check spam or request a new verification email below.
            </p>
          </div>

          <div className="space-y-3">
            <label htmlFor="resend-email" className="block text-sm font-semibold text-slate-300">
              Email address
            </label>
            <input
              id="resend-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="you@example.com"
            />
            <button
              type="button"
              onClick={handleResend}
              disabled={!canResend || isSending}
              className="w-full py-3 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSending ? 'Sending...' : 'Resend verification email'}
            </button>
          </div>

          {message && <p className="text-sm text-emerald-300">{message}</p>}
          {error && <p className="text-sm text-red-300">{error}</p>}

          <div className="text-sm text-slate-300 space-y-1">
            <p>Next steps:</p>
            <p>1. Open the email and click the verification link.</p>
            <p>2. Return and sign in after verification.</p>
          </div>

          <div className="flex items-center justify-between text-sm">
            <Link to="/register" className="text-slate-400 hover:text-slate-300">Use a different email</Link>
            <Link to="/login" className="font-semibold text-indigo-400 hover:text-indigo-300">Go to login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
